import type { Entregador, Pedido, StatusPedido } from './dominio';
import { MENSAGENS_DE_ERRO } from './erros';
import type { PedidoDaFila } from './fila';
import { primeiroNome } from './mensagens';

// "Sai com Carlos" na própria linha da lista (decisão B3): um clique grava a saída, sem confirmação, e o
// Desfazer de 5 s volta. Quem pode sair e os textos da barra do Desfazer moram aqui.

export interface OpcaoDeSaida {
  entregadorId: string;
  nome: string;
  /** "Carlos", ou "Carlos S." quando há dois Carlos ativos. */
  nomeCurto: string;
  /** O pedido já está separado para ele. */
  separado: boolean;
  /** Sem o app pareado: a loja marca o "Entregue" por ele. */
  semAparelho: boolean;
  /** Quantos pedidos dele estão na rua agora. */
  naRua: number;
}

/** Botões "Sai com" na linha; o resto vai para "Outro". */
export const MAX_BOTOES_SAI_COM = 3;

type EntregadorDaSaida = Pick<Entregador, 'id' | 'nome' | 'ativo' | 'pareado'>;

function nomesCurtos(entregadores: readonly EntregadorDaSaida[]): Map<string, string> {
  const vezes = new Map<string, number>();
  for (const e of entregadores) {
    const primeiro = primeiroNome(e.nome).toLocaleLowerCase('pt-BR');
    vezes.set(primeiro, (vezes.get(primeiro) ?? 0) + 1);
  }
  const curtos = new Map<string, string>();
  for (const e of entregadores) {
    const palavras = e.nome.trim().split(/\s+/);
    const primeiro = palavras[0] ?? '';
    const repetido = (vezes.get(primeiro.toLocaleLowerCase('pt-BR')) ?? 0) > 1;
    const inicial = palavras.length > 1 ? palavras[palavras.length - 1]?.charAt(0) : undefined;
    curtos.set(
      e.id,
      repetido && inicial ? `${primeiro} ${inicial.toLocaleUpperCase('pt-BR')}.` : primeiro,
    );
  }
  return curtos;
}

/** Só ativos; o separado primeiro, depois pelo nome (estável). */
export function opcoesDeSaida(
  entregadores: readonly EntregadorDaSaida[],
  pedido: Pick<Pedido, 'entregadorId'>,
  naRua: ReadonlyMap<string, number>,
): OpcaoDeSaida[] {
  const ativos = entregadores.filter((e) => e.ativo);
  const curtos = nomesCurtos(ativos);
  return ativos
    .map((e) => ({
      entregadorId: e.id,
      nome: e.nome,
      nomeCurto: curtos.get(e.id) ?? e.nome,
      separado: e.id === pedido.entregadorId,
      semAparelho: !e.pareado,
      naRua: naRua.get(e.id) ?? 0,
    }))
    .sort(
      (a, b) => Number(b.separado) - Number(a.separado) || a.nome.localeCompare(b.nome, 'pt-BR'),
    );
}

type PedidoDoTexto = Pick<PedidoDaFila, 'numero' | 'clienteNome'>;

/** "Nº 1093, Edson Cardoso, saiu com Carlos." Use o nome do botão (OpcaoDeSaida.nomeCurto). */
export function textoDaSaida(p: PedidoDoTexto, entregadorNome: string): string {
  return `Nº ${p.numero}, ${p.clienteNome}, saiu com ${entregadorNome.trim()}.`;
}

/** "Nº 1089, João Pereira, entregue." */
export function textoDaEntrega(p: PedidoDoTexto): string {
  return `Nº ${p.numero}, ${p.clienteNome}, entregue.`;
}

/** "Nº 1089, João Pereira, voltou para a loja." (o pedido a caminho que volta, com Desfazer). */
export function textoDaVolta(p: PedidoDoTexto): string {
  return `Nº ${p.numero}, ${p.clienteNome}, voltou para a loja.`;
}

/**
 * Por que o "Sai com" não gravou: a escrita só vale com o pedido ainda na loja (SaidaRecusada traz o
 * pedido como está agora; null = não achou, ou é de outra loja).
 */
export function textoDaSaidaRecusada(
  atual: { status: StatusPedido; entregadorNome: string | null } | null,
): string {
  if (!atual) return MENSAGENS_DE_ERRO.pedido_nao_encontrado;
  if (atual.status === 'a_caminho') {
    return atual.entregadorNome
      ? `O ${primeiroNome(atual.entregadorNome)} já saiu com este pedido pelo app.`
      : 'Este pedido já saiu para entrega.';
  }
  if (atual.status === 'cancelado') return 'Este pedido foi cancelado.';
  if (atual.status === 'entregue') return 'Este pedido já foi entregue.';
  return MENSAGENS_DE_ERRO.transicao_invalida;
}
